import React, { FC, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Collapse, Divider, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { formatCurrency } from "../../utils/formatCurrency";
import { useUser } from "../../hooks/useUser";
import { hidePricing } from "../../utils";

export interface ProjectPricingBreakdown {
    contentTypeId: number | null;
    contentTypeBase: number;
    productCount: number;
    perProductBase: number;
    productTotal: number;
    // Legacy snapshot fields: per-project product/channel volume discounts
    // were retired; snapshots priced before that still carry them.
    productSubtotal?: number;
    productDiscountPct?: number;
    channelDiscountPct?: number;
    // The content type's cost sheet, frozen at snapshot time (absent on
    // snapshots from before cost line items existed).
    baseCostLineItems?: {
        label: string;
        hours: number | null;
        rate: number | null;
        cost: number;
        notes?: string | null;
    }[];
    confirmedExpertCount: number;
    // This project's share of the expert cost. A confirmed rate is flat for
    // the whole engagement (TDC-1563), so on a shared selection process each
    // project carries expertRatesTotal / processProjectCount.
    expertTotal: number;
    // Undivided sum of the confirmed flat rates on the selection process, and
    // the divisor used for the share. Absent on snapshots from before the
    // flat-rate change (those billed the full rates on every project).
    expertRatesTotal?: number;
    processProjectCount?: number;
    // True when expertTotal is the placeholder for one planned expert (uses
    // experts but none confirmed yet).
    usingDefaultExpert?: boolean;
    // True when the price is locked ("actual"); otherwise it's an estimate.
    priceLocked?: boolean;
    channelCount: number;
    channelSubtotal: number;
    optionFanout: number;
    channelTotal: number;
    licenseKey: string | null;
    licenseTotal: number;
    preDiscountTotal: number;
    projectCount: number;
    projectCountDiscountPct: number;
    role?: "distinct" | "matching";
    distinctCount?: number;
    matchingDiscountPct?: number;
    discountPct?: number;
    // Price after tier discounts but before the manual adjustment. Present on
    // snapshots from the updated recompute; older ones omit it.
    discountedTotal?: number;
    // Internal-only manual adjustment (signed dollars). total = discountedTotal + productionAdjustment.
    productionAdjustment?: number;
    total: number;
    // Light marker set when the project's order sits under a packaged SOW.
    // Brand surfaces show this instead of the à-la-carte estimate.
    package?: {
        name: string;
        key?: string;
        sellPrice: number;
        coveredByPackage?: boolean;
    };
}

interface Props {
    pricingTotal: string | null | undefined;
    pricingBreakdown: ProjectPricingBreakdown | null | undefined;
    pricingSnapshottedAt?: string | null;
    defaultExpanded?: boolean;
    /**
     * The priced project (or any entity carrying brands[]/brand with rmn
     * flags). TDC-1591: RMN-deal work shows no pricing at all.
     */
    project?: any;
}

const Row: FC<{ label: string; value: string; muted?: boolean; bold?: boolean }> = ({ label, value, muted, bold }) => (
    <Box className="flex justify-between items-baseline py-1">
        <Typography
            sx={{
                fontSize: "14px",
                color: muted ? "var(--muted)" : "var(--text-2)",
                fontWeight: bold ? 700 : 450,
                fontFamily: "CircularStd"
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: "14px",
                color: muted ? "var(--muted)" : "var(--text)",
                fontWeight: bold ? 700 : 500,
                fontFamily: "CircularStd"
            }}
        >
            {value}
        </Typography>
    </Box>
);

// "Content type base" row with a toggle revealing the cost sheet frozen onto
// the quote snapshot (baseCostLineItems). Bill-side numbers only — the sheet
// sums to the base price. Snapshots from before cost sheets existed carry no
// items, and render as a plain row instead (no static fallback: the old deck
// recipe holds INTERNAL cost rates and must not surface here). The sheet
// always starts collapsed.
interface BaseCostItem {
    label: string;
    hours: number | null;
    rate: number | null;
    cost: number;
    notes?: string | null;
}

const ContentTypeBaseRow: FC<{
    value: number;
    items: BaseCostItem[];
}> = ({ value, items }) => {
    const [open, setOpen] = useState(false);
    const sheet = items;
    const sheetTotal = sheet.reduce((s, r) => s + r.cost, 0);
    return (
        <>
            <Box
                component="button"
                type="button"
                aria-expanded={open}
                className="flex justify-between items-baseline py-1"
                sx={{
                    cursor: "pointer",
                    width: "100%",
                    background: "none",
                    border: 0,
                    p: 0,
                    textAlign: "left",
                    font: "inherit",
                    color: "inherit"
                }}
                onClick={() => setOpen((v) => !v)}
            >
                <Typography sx={{ fontSize: "14px", color: "var(--text-2)", fontWeight: 450, fontFamily: "CircularStd" }}>
                    Content type base{" "}
                    <Typography component="span" sx={{ fontSize: "12px", color: "var(--accent-text)", fontFamily: "CircularStd" }}>
                        {open ? "hide cost sheet ▴" : "what's behind this? ▾"}
                    </Typography>
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "var(--text)", fontWeight: 500, fontFamily: "CircularStd" }}>
                    {formatCurrency(value)}
                </Typography>
            </Box>
            <Collapse in={open}>
                <Box sx={{ pl: 1.5, pb: 0.5, borderLeft: "2px solid var(--border)", ml: 0.5, mb: 0.5 }}>
                    {sheet.map((r, i) => (
                        <Box key={`${r.label}-${i}`} className="flex justify-between items-baseline" sx={{ py: "1px" }}>
                            <Typography sx={{ fontSize: "12px", color: "var(--muted)", fontFamily: "CircularStd" }}>
                                {r.label}
                                {r.hours != null && r.rate != null
                                    ? ` · ${r.hours} hrs × $${r.rate}`
                                    : ""}
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "var(--muted)", fontFamily: "CircularStd" }}>
                                {formatCurrency(r.cost)}
                            </Typography>
                        </Box>
                    ))}
                    <Box className="flex justify-between items-baseline" sx={{ py: "1px" }}>
                        <Typography sx={{ fontSize: "12px", color: "var(--text-2)", fontWeight: 700, fontFamily: "CircularStd" }}>
                            Cost basis
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "var(--text-2)", fontWeight: 700, fontFamily: "CircularStd" }}>
                            {formatCurrency(sheetTotal)}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "11px", color: "var(--muted)", fontStyle: "italic", fontFamily: "CircularStd", mt: 0.5 }}>
                        The resources the base price is set to cover for this content type.
                    </Typography>
                </Box>
            </Collapse>
        </>
    );
};

/**
 * The line-item body of a project pricing breakdown. Shared by the expandable
 * PricingBreakdown card and the hover tooltip (PriceBreakdownTooltip), so both
 * render identical numbers. The content-type base row expands into the cost
 * sheet frozen on the snapshot, collapsed by default on every surface.
 */
export const ProjectPriceBreakdownContent: FC<{
    breakdown: ProjectPricingBreakdown | null | undefined;
    pricingSnapshottedAt?: string | null;
}> = ({ breakdown: b, pricingSnapshottedAt }) => {
    if (!b) {
        return (
            <Typography sx={{ fontSize: "14px", color: "var(--muted)", fontFamily: "CircularStd" }}>
                Breakdown unavailable.
            </Typography>
        );
    }
    const discounted = b.discountedTotal ?? b.total;
    const discountAmount = b.preDiscountTotal - discounted;
    const isMatching = b.role === "matching" || (b.matchingDiscountPct ?? 0) > 0;

    const header = (
        <>
            {/* Estimated vs actual header + explanation */}
            <Box sx={{ mb: 1 }}>
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: b.priceLocked ? "var(--up)" : "var(--accent-text)",
                        fontFamily: "CircularStd",
                    }}
                >
                    {b.priceLocked ? "Actual price" : "Estimated price"}
                </Typography>
                {!b.priceLocked && (
                    <Typography
                        sx={{
                            fontSize: "11.5px",
                            color: "var(--text-2)",
                            fontFamily: "CircularStd",
                            mt: "2px",
                        }}
                    >
                        Estimated until experts are selected and production adjustments
                        are applied — then the price is locked as the actual price
                        (usually once experts are confirmed and the film date is
                        scheduled).
                    </Typography>
                )}
            </Box>
            <Divider sx={{ mb: 1 }} />
        </>
    );

    // The expandable sheet renders ONLY from the snapshot's own line items.
    // Old snapshots (no items) get a plain row everywhere — the static deck
    // recipe held internal cost rates and must never display as a bill sheet.
    const hasSnapshotSheet = (b.baseCostLineItems?.length ?? 0) > 0;
    const baseRow = hasSnapshotSheet ? (
        <ContentTypeBaseRow value={b.contentTypeBase} items={b.baseCostLineItems!} />
    ) : (
        <Row label="Content type base" value={formatCurrency(b.contentTypeBase)} />
    );

    return (
        <>
            {header}
            {baseRow}
            <QuoteRows
                b={b}
                isMatching={isMatching}
                discountAmount={discountAmount}
                pricingSnapshottedAt={pricingSnapshottedAt}
            />
        </>
    );
};

// The quote body after the content-type base row: products, channels,
// experts, license, then subtotal → discounts → adjustment → total.
const QuoteRows: FC<{
    b: ProjectPricingBreakdown;
    isMatching: boolean;
    discountAmount: number;
    pricingSnapshottedAt?: string | null;
}> = ({ b, isMatching, discountAmount, pricingSnapshottedAt }) => (
    <>
        {b.productCount > 0 && (
                <>
                    <Row
                        label={`Products (${b.productCount} × ${formatCurrency(b.perProductBase)})`}
                        value={formatCurrency(b.productSubtotal ?? b.productTotal)}
                    />
                    {/* Legacy snapshots only — these discounts are retired. */}
                    {(b.productDiscountPct ?? 0) > 0 && (
                        <Row
                            label={`Product tier discount (${b.productDiscountPct}%)`}
                            value={`−${formatCurrency((b.productSubtotal ?? b.productTotal) - b.productTotal)}`}
                            muted
                        />
                    )}
                </>
            )}
            {b.channelCount > 0 && (
                <>
                    <Row
                        label={`Channels (${b.channelCount}${b.optionFanout > 1 ? ` × ${b.optionFanout} options` : ""})`}
                        value={formatCurrency(b.channelSubtotal * b.optionFanout)}
                    />
                    {(b.channelDiscountPct ?? 0) > 0 && (
                        <Row
                            label={`Channel tier discount (${b.channelDiscountPct}%)`}
                            value={`−${formatCurrency(b.channelSubtotal * b.optionFanout - b.channelTotal)}`}
                            muted
                        />
                    )}
                </>
            )}
            {b.confirmedExpertCount > 0 ? (
                <Row
                    label={
                        (b.processProjectCount ?? 1) > 1
                            ? `Confirmed experts (${b.confirmedExpertCount}, ${formatCurrency(b.expertRatesTotal ?? 0)} split across ${b.processProjectCount} projects)`
                            : `Confirmed experts (${b.confirmedExpertCount})`
                    }
                    value={formatCurrency(b.expertTotal)}
                />
            ) : b.usingDefaultExpert ? (
                <Row
                    label="Expert (1, estimated)"
                    value={formatCurrency(b.expertTotal)}
                    muted
                />
            ) : null}
            {b.licenseKey && (
                <Row label={`License (${b.licenseKey})`} value={formatCurrency(b.licenseTotal)} />
            )}
            <Divider sx={{ my: 1 }} />
            <Row label="Subtotal" value={formatCurrency(b.preDiscountTotal)} />
            {isMatching ? (
                <>
                    <Row
                        label={`Repeat-product discount (${b.matchingDiscountPct ?? 0}% off)`}
                        value={`−${formatCurrency(discountAmount)}`}
                        muted
                    />
                    {(b.distinctCount ?? 0) > 0 && (
                        <Typography
                            sx={{
                                fontSize: "12px",
                                color: "var(--muted)",
                                fontFamily: "CircularStd",
                                fontStyle: "italic",
                                mt: -0.5,
                                mb: 0.5
                            }}
                        >
                            {b.distinctCount} distinct product set
                            {b.distinctCount === 1 ? "" : "s"} in this order
                        </Typography>
                    )}
                </>
            ) : b.projectCountDiscountPct > 0 ? (
                <Row
                    label={`Order quantity discount (${b.projectCountDiscountPct}% — ${b.projectCount} projects)`}
                    value={`−${formatCurrency(discountAmount)}`}
                    muted
                />
            ) : null}
            {!!b.productionAdjustment && (
                <Row
                    label="Production adjustment"
                    value={`${b.productionAdjustment > 0 ? "+" : "−"}${formatCurrency(Math.abs(b.productionAdjustment))}`}
                />
            )}
            <Divider sx={{ my: 1 }} />
            <Row label="Total" value={formatCurrency(b.total)} bold />
            {pricingSnapshottedAt && (
                <Typography
                    sx={{
                        fontSize: "12px",
                        color: "var(--muted)",
                        fontFamily: "CircularStd",
                        mt: 2,
                        fontStyle: "italic"
                    }}
                >
                    Quoted {moment(pricingSnapshottedAt).fromNow()} ({moment(pricingSnapshottedAt).format("MMM D, YYYY")})
                </Typography>
            )}
    </>
);

export const PricingBreakdown: FC<Props> = ({
    pricingTotal,
    pricingBreakdown,
    pricingSnapshottedAt,
    defaultExpanded = false,
    project,
}) => {
    const { user, role } = useUser();

    // RMN-deal work is hidden from everyone (TDC-1591) and all pricing is
    // hidden from brand-side viewers (TDC-1578) — locked "actual" prices
    // included. Internal staff on non-RMN work are the only readers.
    if (hidePricing(role, user, project)) {
        return null;
    }

    if (pricingTotal === null || pricingTotal === undefined) {
        return (
            <Box className="flex flex-col gap-2 p-4 border border-border-token rounded-2xl bg-surface">
                <Typography sx={{ fontSize: "14px", color: "var(--muted)", fontFamily: "CircularStd" }}>
                    Pricing not yet calculated — save this project to generate.
                </Typography>
            </Box>
        );
    }

    const total = formatCurrency(pricingTotal);
    const b = pricingBreakdown;

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            sx={{
                boxShadow: "none",
                border: "1px solid var(--border)",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    borderRadius: "12px",
                    minHeight: "56px",
                    "&.Mui-expanded": { minHeight: "56px" },
                    "& .MuiAccordionSummary-content": { margin: "12px 0" },
                    "& .MuiAccordionSummary-content.Mui-expanded": { margin: "12px 0" }
                }}
            >
                <Box className="flex w-full justify-between items-baseline pr-2">
                    <Typography
                        sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: "15px", color: "var(--text)" }}
                    >
                        Total
                    </Typography>
                    <Typography
                        sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: "18px", color: "var(--text)" }}
                    >
                        {total}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: "20px", pt: 0 }}>
                <ProjectPriceBreakdownContent
                    breakdown={b}
                    pricingSnapshottedAt={pricingSnapshottedAt}
                />
            </AccordionDetails>
        </Accordion>
    );
};
